import * as React from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { useToast } from "@/hooks/use-toast";
import { format, isValid } from "date-fns";
import { Check, X, CalendarIcon, ClockIcon, BuildingIcon, MapPin, DollarSign, MessageSquare, BookOpen, Phone, Mail } from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { getAllEventInvites, type InviteType as RawInvite } from "@/lib/queries/invites";
import { LoadingDialog } from "@/components/ui/loading-dialog";
import { Badge } from "@/components/ui/badge";

interface EventInvite {
  id: string;
  status: string;
  created_at: string;
  event: {
    event_name: string;
    description: string;
    event_date: string;
    event_time: string;
    corporate: {
      org_name: string;
      poc_first_name?: string;
      poc_last_name?: string;
      email?: string;
      phone?: string;
      isd_code?: string;
    };
    location?: string;
    paylater?: boolean;
    remarks?: string;
    meta?: {
      currency?: string;
      timezone?: string;
      team_name?: string;
      event_cost?: number;
      npo_location?: string;
    };
    opportunity_catalog?: {
      title?: string;
      duration?: string;
      format?: string;
    };
  };
}

interface Props {
  type: string;
  session: any | null;
  profile: any | null;
}

export function EventInvites({ type, session, profile }: Props) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [isLoading, setIsLoading] = React.useState(false);
  const [activeTab, setActiveTab] = React.useState<string>("pending");

  const { data: invites = [], isLoading: isInvitesLoading } = useQuery<RawInvite[], Error, EventInvite[]>({
    queryKey: ["event-invites", type, profile?.id],
    queryFn: () => getAllEventInvites(type as "npo" | "host", session?.access_token),
    enabled: !!session,
    select: (rawInvites: RawInvite[]) => (
      (rawInvites as any[]).map((invite: any) => ({
        id: invite.id,
        status: invite.status,
        created_at: invite.created_at,
        event: {
          event_name: invite.event?.event_name || invite.event_name,
          description: invite.event?.description || invite.description,
          event_date: invite.event?.event_date || invite.event_date,
          event_time: invite.event?.event_time || invite.event_time,
          corporate: {
            org_name: invite.event?.corporate?.org_name || invite.corporate_profiles?.company_name || "No company specified",
            poc_first_name: invite.event?.corporate?.poc_first_name,
            poc_last_name: invite.event?.corporate?.poc_last_name,
            email: invite.event?.corporate?.email,
            phone: invite.event?.corporate?.phone,
            isd_code: invite.event?.corporate?.isd_code
          },
          location: invite.event?.location || invite.location,
          paylater: invite.event?.paylater !== undefined ? invite.event.paylater : invite.paylater,
          remarks: invite.event?.remarks || invite.remarks,
          meta: invite.event?.meta || invite.meta,
          opportunity_catalog: invite.event?.opportunity_catalog || invite.opportunity_catalog
        }
      }))
    ) as EventInvite[]
  });

  const filteredInvites = React.useMemo(() => {
    return invites.filter(invite => invite.status === activeTab);
  }, [invites, activeTab]);

  const updateInvite = useMutation({
    mutationFn: async ({ id, status }: { id: string; status: string }) => {
      setIsLoading(true);
      const invite = invites.find(invite => invite.id === id);
      if (!invite) throw new Error('Invite not found');

      // Call event-lifecycle endpoint
      const response = await fetch(`${import.meta.env.VITE_CC_API_URL}/v1/hooks/event-lifecycle`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session?.access_token}`,
        },
        body: JSON.stringify({
          action: status === 'accepted' 
            ? (type === 'npo' ? 'npo_accepted' : 'host_accepted')
            : (type === 'npo' ? 'npo_rejected' : 'host_rejected'),
          eventId: invite.event.id,
          inviteId: id,
          remarks: `${type.toUpperCase()} ${status} the event`
        })
      });

      if (!response.ok) {
        throw new Error('Failed to update event lifecycle');
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["event-invites", type, profile?.id] });
      toast({
        title: "Success",
        description: "Invite status updated successfully",
      });
      setIsLoading(false);
    },
    onError: (error) => {
      toast({
        title: "Error",
        description: error instanceof Error ? error.message : "Failed to update invite status",
        variant: "destructive",
      });
      setIsLoading(false);
    },
  });

  const handleInviteResponse = (id: string, status: string) => {
    updateInvite.mutate({ id, status });
  };

  const getStatusText = (status: string) => {
    return status.charAt(0).toUpperCase() + status.slice(1);
  };

  const renderInvites = () => {
    if (isInvitesLoading) {
      return <div>Loading...</div>;
    }

    if (filteredInvites.length === 0) {
      return <div className="text-center text-muted-foreground">No {getStatusText(activeTab)} Invites</div>;
    }

    return filteredInvites.map((invite) => (
      <Card key={invite.id} className="mb-4">
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <CardTitle className="text-lg">{invite.event.event_name}</CardTitle>
            <Badge
              variant={invite.status === "pending" ? "outline" : invite.status === "accepted" ? "success" : "destructive"}
            >
              {getStatusText(invite.status)}
            </Badge>
          </div>
          <CardDescription className="mt-2">{invite.event.description}</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-2">
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <div className="flex items-center gap-2">
                <CalendarIcon className="w-4 h-4" />
                <span>{isValid(new Date(invite.event.event_date)) ? format(new Date(invite.event.event_date), "PPP") : invite.event.event_date}</span>
              </div>
              <div className="flex items-center gap-2 ml-4">
                <ClockIcon className="w-4 h-4" />
                <span>{isValid(new Date(invite.event.event_time)) ? format(new Date(invite.event.event_time), "p") : invite.event.event_time}</span>
              </div>
            </div>
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <BuildingIcon className="w-4 h-4" />
              <span>{invite.event.corporate?.org_name || "No company specified"}</span>
            </div>

            {/* Additional event details for RSVP in a horizontal layout */}
            <div className="flex flex-wrap gap-2 mt-2">
              {invite.event.location && (
                <div className="flex items-center gap-1 bg-blue-100 text-blue-700 px-2 py-1 rounded">
                  <MapPin className="w-4 h-4" />
                  <span>{invite.event.location}</span>
                </div>
              )}
              {invite.event.paylater !== undefined && (
                <div className="flex items-center gap-1 bg-green-100 text-green-700 px-2 py-1 rounded">
                  <DollarSign className="w-4 h-4" />
                  <span>{invite.event.paylater ? "Pay Later" : "Pay Now"}</span>
                </div>
              )}
              {invite.event.remarks && (
                <div className="flex items-center gap-1 bg-purple-100 text-purple-700 px-2 py-1 rounded">
                  <MessageSquare className="w-4 h-4" />
                  <span>{invite.event.remarks}</span>
                </div>
              )}
              {invite.event.meta && (
                <div className="flex items-center gap-1 bg-yellow-100 text-yellow-700 px-2 py-1 rounded">
                  <DollarSign className="w-4 h-4" />
                  <span>{invite.event.meta.currency} {invite.event.meta.event_cost}</span>
                </div>
              )}
              {invite.event.meta && invite.event.meta.timezone && (
                <div className="flex items-center gap-1 bg-gray-100 text-gray-700 px-2 py-1 rounded">
                  <ClockIcon className="w-4 h-4" />
                  <span>{invite.event.meta.timezone}</span>
                </div>
              )}
              {invite.event.opportunity_catalog && invite.event.opportunity_catalog.title && (
                <div className="flex items-center gap-1 bg-indigo-100 text-indigo-700 px-2 py-1 rounded">
                  <BookOpen className="w-4 h-4" />
                  <span>{invite.event.opportunity_catalog.title}</span>
                </div>
              )}
            </div>

            {/* Booking details section */}
            {invite.event.corporate && (
              <div className="mt-4">
                <div className="text-sm font-semibold mb-1">Booking details</div>
                <div className="flex flex-wrap gap-2">
                  <div className="flex items-center gap-1">
                    <span className="text-[0.875rem] text-muted-foreground">Booked by:</span>
                    <div className="flex items-center gap-1 bg-blue-50 text-blue-800 px-2 py-1 rounded">
                      <BuildingIcon className="w-4 h-4" />
                      <span>{invite.event.corporate.org_name}</span>
                    </div>
                  </div>
                  {invite.event.corporate.poc_first_name && invite.event.corporate.poc_last_name && (
                    <div className="flex items-center gap-1 mt-2 bg-indigo-50 text-indigo-800 px-2 py-1 rounded">
                      <span>{invite.event.corporate.poc_first_name} {invite.event.corporate.poc_last_name}</span>
                    </div>
                  )}
                  {invite.event.corporate.email && (
                    <div className="flex items-center gap-1 mt-2 bg-green-50 text-green-800 px-2 py-1 rounded">
                      <Mail className="w-4 h-4" />
                      <span>{invite.event.corporate.email}</span>
                    </div>
                  )}
                  {invite.event.corporate.phone && (
                    <div className="flex items-center gap-1 mt-2 bg-yellow-50 text-yellow-800 px-2 py-1 rounded">
                      <Phone className="w-4 h-4" />
                      <span>{invite.event.corporate.isd_code}{invite.event.corporate.phone}</span>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* RSVP buttons remain unchanged */}
            {invite.status === "pending" && (
              <div className="flex justify-end gap-2 pt-2">
                <Button
                  variant="outline"
                  onClick={() => handleInviteResponse(invite.id, "declined")}
                  className="text-red-500 hover:text-red-600"
                >
                  <X className="w-4 h-4 mr-1" />
                  Decline
                </Button>
                <Button
                  onClick={() => handleInviteResponse(invite.id, "accepted")}
                >
                  <Check className="w-4 h-4 mr-1" />
                  Accept
                </Button>
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    ));
  };

  return (
    <>
      <LoadingDialog 
        open={isLoading} 
        title="Updating Invite"
        description="Please wait while we update your response..."
      />
      <Tabs defaultValue="pending" className="w-full" onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="pending">
            {getStatusText("pending")} ({invites.filter(invite => invite.status === "pending").length})
          </TabsTrigger>
          <TabsTrigger value="accepted">
            {getStatusText("accepted")} ({invites.filter(invite => invite.status === "accepted").length})
          </TabsTrigger>
          <TabsTrigger value="declined">
            {getStatusText("declined")} ({invites.filter(invite => invite.status === "declined").length})
          </TabsTrigger>
        </TabsList>
        <TabsContent value="pending" className="mt-6">
          {renderInvites()}
        </TabsContent>
        <TabsContent value="accepted" className="mt-6">
          {renderInvites()}
        </TabsContent>
        <TabsContent value="declined" className="mt-6">
          {renderInvites()}
        </TabsContent>
      </Tabs>
    </>
  );
}
